import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { AppHeaderConfig } from '@/contexts/AppHeaderContext';
import { useSpaceRulesOverview } from '@/hooks/queries/useSpaceRulesOverview';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { SpaceRulesOverview } from '@/types/spaces';
import { SpaceRulesCard } from './SpaceRulesCard';
import { SpaceRulesHelp } from './SpaceRulesHelp';

export function SpaceRulesPage() {
  const { userId } = useAuth();
  return <section className="space-y-4 text-text-primary sm:space-y-5">
    <AppHeaderConfig hidePeriodPicker />
    <PageHeader title="Espaços e regras de envio" titleAs="h1" titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
      description="Você decide quais dos seus lançamentos vão para cada espaço. Cada pessoa tem as próprias regras; o espaço só define quem participa, com que parte e que permissão." />
    <RulesContent key={userId ?? 'signed-out'} />
    <SpaceRulesHelp />
  </section>;
}

function RulesContent() {
  const { data, error, isValidating, mutate, changingRuleId, toggleRule } = useSpaceRulesOverview();
  const handleRetry = () => { void mutate().catch(() => undefined); };
  const renderSpace = (entry: SpaceRulesOverview) => <SpaceRulesCard key={entry.space.id} entry={entry}
    refreshing={isValidating || Boolean(changingRuleId)} changingRuleId={changingRuleId} onToggle={toggleRule} />;
  if (error) return <div role="alert" className="space-y-3 rounded-3xl border border-border-subtle bg-surface p-5">
    <p>Não foi possível atualizar seus espaços e regras. Tente novamente para ver os dados atuais.</p>
    <Button variant="outline" onClick={handleRetry} disabled={isValidating}>{isValidating ? 'Atualizando…' : 'Tentar novamente'}</Button>
  </div>;
  if (!data) return <div aria-busy="true" aria-label="Carregando espaços e regras" className="grid gap-4 lg:grid-cols-2">
    <Skeleton className="h-80 rounded-3xl" /><Skeleton className="h-80 rounded-3xl" />
  </div>;
  if (!data.spaces.length) return <div className="space-y-3 rounded-3xl border border-border-subtle bg-surface p-5">
    <h2 className="font-semibold">Você ainda não participa de um espaço</h2>
    <p className="text-sm text-text-secondary">Crie um espaço para dividir despesas. Depois, escolha quais lançamentos enviar na lista de transações.</p>
    <Button asChild><Link to="/spaces/new">Criar espaço</Link></Button>
  </div>;
  return <div className="grid items-start gap-4 lg:grid-cols-2">{data.spaces.map(renderSpace)}</div>;
}
